/**
 * Conversion Tracking Hook
 * 
 * This hook provides a clean interface for firing conversion events
 * across the application. It handles all the complexity of script injection
 * and tracking configuration management.
 * 
 * Usage:
 * ```typescript
 * const { fireConversion } = useConversionTracking();
 * 
 * // Fire a purchase conversion
 * fireConversion('purchase', { value: 99.99, currency: 'USD' });
 * 
 * // Fire a lead conversion
 * fireConversion('lead');
 * ```
 */

import React, { useEffect, useRef } from 'react';
import { ConversionEventType, getTrackingConfig, TenantTrackingConfig } from '../constants/tracking';

// Extend Window interface for global tenant/store IDs
declare global {
    interface Window {
        __TENANT_ID__?: number;
        __STORE_ID__?: number;
    }
}

interface ConversionOptions {
    value?: number;
    currency?: string;
    orderId?: string;
    products?: Array<{
        id: string;
        name: string;
        price: number;
        quantity: number;
    }>;
}

interface UseConversionTrackingReturn {
    /**
     * Fire a conversion event
     * @param eventType - Type of conversion event (purchase, lead, signup, page_view)
     * @param options - Optional conversion data (value, currency, etc.)
     */
    fireConversion: (eventType: ConversionEventType, options?: ConversionOptions) => void;
}

export function useConversionTracking(tenantId?: number, storeId?: number): UseConversionTrackingReturn {
    const scriptsInjectedRef = useRef<Set<string>>(new Set());

    /**
     * Dynamically inject tracking scripts into the DOM
     */
    const injectScript = (content: string, key: string): void => {
        // Prevent duplicate script injections
        if (scriptsInjectedRef.current.has(key)) {
            return;
        }

        try {
            const script = document.createElement('script');
            script.innerHTML = content;
            script.type = 'text/javascript';
            document.head.appendChild(script);

            scriptsInjectedRef.current.add(key);

            // Clean up after 5 seconds to prevent memory leaks
            setTimeout(() => {
                if (script.parentNode) {
                    script.parentNode.removeChild(script);
                }
                scriptsInjectedRef.current.delete(key);
            }, 5000);
        } catch (error) {
            console.error('Failed to inject conversion tracking script:', error);
        }
    };

    /**
     * Fire a conversion event
     */
    const fireConversion = (eventType: ConversionEventType, options?: ConversionOptions): void => {
        // Get current tenant ID from context if not provided
        const currentTenantId = tenantId || window.__TENANT_ID__;
        const currentStoreId = storeId || window.__STORE_ID__;

        if (!currentTenantId) {
            console.warn('Conversion tracking: No tenant ID provided');
            return;
        }

        const trackingConfig = getTrackingConfig(currentTenantId, currentStoreId);

        if (!trackingConfig) {
            console.warn(`Conversion tracking: No configuration found for tenant ${currentTenantId}`);
            return;
        }

        const conversionScripts = generateConversionScripts(trackingConfig, eventType, options);

        if (conversionScripts.length === 0) {
            console.warn(`Conversion tracking: No conversion scripts configured for event type: ${eventType}`);
            return;
        }

        // Inject all conversion scripts
        conversionScripts.forEach((script) => {
            if (script.type === 'script') {
                injectScript(script.content, script.key);
            }
        });

    };

    return {
        fireConversion
    };
}

/**
 * Helper function to generate conversion tracking scripts
 * 
 * @param config - The tenant tracking configuration
 * @param eventType - The type of conversion event
 * @param options - Additional conversion options (value, currency)
 * @returns Array of script elements for conversion tracking
 */
function generateConversionScripts(
    config: TenantTrackingConfig,
    eventType: ConversionEventType,
    options?: { value?: number; currency?: string }
): Array<{ type: 'script' | 'noscript', content: string, key: string }> {
    const scripts: Array<{ type: 'script' | 'noscript', content: string, key: string }> = [];

    if (!config.conversionEvents) return scripts;

    const conversionEvent = config.conversionEvents.find(event => event.type === eventType);
    if (!conversionEvent) return scripts;

    // Find Google Ads tracking configuration
    const googleAdsConfig = config.tracking.find(t => t.type === 'google-ads' || t.type === 'google-tag');
    const googleAnalyticsConfig = config.tracking.find(t => t.type === 'google-analytics');
    const ampAnalyticsConfig = config.tracking.find(t => t.type === 'amp-analytics');

    // Google Ads conversion tracking
    if (googleAdsConfig && conversionEvent.conversionLabel) {
        const conversionId = googleAdsConfig.id;
        const conversionLabel = conversionEvent.conversionLabel;

        // Standard gtag conversion
        scripts.push({
            type: 'script',
            content: `gtag('event', 'conversion', {'send_to': '${conversionId}/${conversionLabel}'});`,
            key: 'google-ads-conversion'
        });

        // AMP Analytics conversion
        if (ampAnalyticsConfig) {
            scripts.push({
                type: 'script',
                content: `
                <amp-analytics type="gtag" data-credentials="include">
                <script type="application/json">
                { 
                    "vars": { 
                        "gtag_id": "${conversionId}", 
                        "config": { 
                            "${conversionId}": { "groups": "default" } 
                        } 
                    }, 
                    "triggers": {
                        "C_KsEUDdlFXOk": { 
                            "on": "visible", 
                            "vars": { 
                                "event_name": "conversion", 
                                "send_to": ["${conversionId}/${conversionLabel}"] 
                            } 
                        }
                    } 
                }
                </script>
                </amp-analytics>`,
                key: 'amp-analytics-conversion'
            });
        }
    }

    return scripts;
}

/**
 * Higher-order component for automatic conversion tracking
 * 
 * Usage:
 * ```typescript
 * const OrderConfirmationWithTracking = withConversionTracking(
 *   OrderConfirmation,
 *   'purchase',
 *   (props) => ({ value: props.orderTotal, orderId: props.orderId })
 * );
 * ```
 */
export function withConversionTracking<P extends object>(
    Component: React.ComponentType<P>,
    eventType: ConversionEventType,
    getOptions?: (props: P) => ConversionOptions
): React.ComponentType<P> {
    const ConversionTrackingWrapper: React.ComponentType<P> = (props: P) => {
        const { fireConversion } = useConversionTracking();

        React.useEffect(() => {
            const options = getOptions ? getOptions(props) : undefined;
            fireConversion(eventType, options);
        }, [props, fireConversion, getOptions, eventType]);

        return React.createElement(Component, props);
    };

    return ConversionTrackingWrapper;
}
